import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, Grid, Label, Modal } from 'semantic-ui-react';
import isEmpty from 'lodash/isEmpty';
import CryptoBuySellCorridorConfigurationForm from './CryptoBuySellCorridorConfigurationForm';
import {
  CryptoBuySellCorridorConfiguration,
  StateCryptoBuySellCorridorConfiguration,
  validateCryptoBuySellCorridorConfiguration,
} from '../../../../types/models/CryptoBuySellCorridorConfiguration';
import { PayoutMethod } from '../../../../types/enums/PayoutMethod';
import { PaymentMethod } from '../../../../types/enums/PaymentMethod';
import { CustomerType } from '../../../../types/enums/CustomerType';
import { TransferDirection } from '../../../../types/enums/TransferDirection';

interface CryptoBuySellCorridorCreateFormProps {
  defaultValues?: Partial<CryptoBuySellCorridorConfiguration>;
  existsConfigurations?: CryptoBuySellCorridorConfiguration[];
  transferDirection: keyof typeof TransferDirection;
  onSubmit: (corridorConfig: CryptoBuySellCorridorConfiguration) => void;
  onClose: () => void;
}

export default function CryptoBuySellCorridorCreateForm({
  defaultValues = undefined,
  existsConfigurations = [],
  transferDirection,
  onSubmit,
  onClose,
}: CryptoBuySellCorridorCreateFormProps): JSX.Element {
  const initialState = useMemo<StateCryptoBuySellCorridorConfiguration>(
    () => ({
      ...{
        countryId: null,
        currencyFromIds: [],
        currencyToIds: [],
        paymentMethods: [PaymentMethod.BANK_ACCOUNT],
        payoutMethods: [PayoutMethod.BANK_ACCOUNT],
        paymentDetails: '',
        payoutDetails: '',
        customerType: CustomerType.NOT_SPECIFIED,
        transactionType: null,
      },
      ...(defaultValues || {}),
    }),
    [defaultValues],
  );

  const [state, setState] =
    useState<StateCryptoBuySellCorridorConfiguration>(initialState);
  const validationErrors = useMemo(
    () =>
      validateCryptoBuySellCorridorConfiguration(state, existsConfigurations),
    [state, existsConfigurations],
  );
  const canSubmit = isEmpty(validationErrors);
  const onChange = useCallback(
    (changed) => setState((prev: any) => ({ ...prev, ...changed })),
    [setState],
  );
  const onClickSubmitButton = useCallback(() => {
    onSubmit(state as CryptoBuySellCorridorConfiguration);
    setState(initialState);
  }, [onSubmit, state, setState, initialState]);
  return (
    <Modal closeIcon open onClose={onClose}>
      <Modal.Header content="Add New Table" />
      <Modal.Content>
        <Grid>
          <Grid.Row>
            <Grid.Column>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <CryptoBuySellCorridorConfigurationForm
                  {...state}
                  onChange={onChange}
                  validationErrors={validationErrors}
                  transferDirection={transferDirection}
                />
              </Form>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column textAlign="right" className="action-buttons">
              {validationErrors.sameConfiguration && (
                <Label basic color="red" style={{ marginRight: '1em' }}>
                  {validationErrors.sameConfiguration}
                </Label>
              )}
              <Button
                type="button"
                color="blue"
                onClick={onClickSubmitButton}
                disabled={!canSubmit}
                data-qa="add-table"
              >
                Add Table
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
    </Modal>
  );
}
